import { Button } from "@readouts/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@readouts/ui/form";
import { Input } from "@readouts/ui/input";
import { Loader2Icon, MailIcon, XIcon } from "lucide-react";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { Link } from "react-router-dom";
import { ReactComponent as Logo } from "../images/logo-dark-text.svg";

type FormValues = {
  email: string;
};

export function RequestPasswordReset() {
  const [sent, setSent] = useState(false);

  const form = useForm<FormValues>({
    defaultValues: { email: "" },
  });

  async function onSubmit(values: FormValues) {
    const res = await fetch(
      `/api/v1/password/request-password-reset?email=${values.email}`,
      {
        method: "GET",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
      },
    );

    if (!res.ok) {
      form.setError("root", {
        message: "Něco se nepovedlo. Zkuste to znovu.",
      });
      return;
    }

    // Always show the same success state regardless of whether the email
    // exists, so we don't leak account existence to an attacker.
    setSent(true);
  }

  const [hostname, setHostname] = useState("readouts.cloud");

  useEffect(() => {
    if (typeof window !== "undefined") {
      if (window.location.hostname === "localhost") {
        setHostname("localhost:3000");
        return;
      }
      setHostname(window.location.hostname.replace("identity.", ""));
    }
  }, []);

  return (
    <main className="w-screen h-dvh flex justify-center items-center bg-background relative">
      <title>Obnovení hesla</title>

      <div className="w-96 bg-white rounded-2xl shadow-lg">
        <div className="p-5 flex flex-col justify-center gap-8 w-full">
          <div className="flex justify-center flex-col items-center gap-8">
            <Logo width={150} height={56} />
          </div>

          {sent ? (
            <div className="flex flex-col items-center gap-4 text-center py-2">
              <div className="flex items-center justify-center h-10 w-10 rounded-full bg-emerald-100">
                <MailIcon className="h-5 w-5 text-emerald-600" />
              </div>
              <div className="flex flex-col gap-1">
                <p className="text-sm font-medium text-slate-900">
                  Zkontrolujte si e-mail
                </p>
                <p className="text-sm text-slate-500">
                  Pokud pro{" "}
                  <span className="font-medium text-slate-700">
                    {form.getValues("email")}
                  </span>{" "}
                  existuje účet, poslali jsme na něj odkaz pro obnovení hesla.
                </p>
              </div>
              <Link
                to="/login"
                className="text-sm mt-1 font-medium text-gray-700"
                onClick={() => setSent(false)}
              >
                Zpět na přihlášení
              </Link>
            </div>
          ) : (
            <Form {...form}>
              <form
                onSubmit={form.handleSubmit(onSubmit)}
                className="flex flex-col gap-4"
              >
                <div className="flex flex-col gap-1 mb-1">
                  <h1 className="text-base font-semibold text-slate-900">
                    Zapomněli jste heslo?
                  </h1>
                  <p className="text-sm text-slate-500">
                    Zadejte svůj e-mail a my vám zašleme odkaz pro obnovení
                    hesla.
                  </p>
                </div>

                <FormField
                  control={form.control}
                  name="email"
                  rules={{
                    required: "E-mail je povinný",
                    pattern: {
                      value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                      message: "Zadejte platnou e-mailovou adresu",
                    },
                  }}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-[10px] uppercase tracking-wide text-slate-500">
                        E-mail
                      </FormLabel>
                      <FormControl>
                        <Input
                          type="email"
                          autoComplete="email"
                          autoFocus
                          className="w-full"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div className="flex flex-col gap-2">
                  <Button type="submit" disabled={form.formState.isSubmitting}>
                    {form.formState.isSubmitting ? (
                      <Loader2Icon className="animate-spin text-white" />
                    ) : form.formState.errors.root ? (
                      <XIcon className="text-white" />
                    ) : (
                      "Odeslat odkaz pro obnovení"
                    )}
                  </Button>
                  {form.formState.errors.root && (
                    <p className="text-sm text-red-500 text-center">
                      {form.formState.errors.root.message}
                    </p>
                  )}
                </div>

                <Link
                  to="/login"
                  className="text-xs text-center text-slate-400 hover:text-slate-600 underline"
                >
                  Zpět na přihlášení
                </Link>
              </form>
            </Form>
          )}
        </div>
      </div>
      <span className="absolute bottom-2 text-xs text-muted-foreground">
        ©{new Date().getFullYear()} QONIQO s.r.o.
      </span>
    </main>
  );
}
